import React, { useContext, createContext, useReducer } from "react";
import PropTypes from "prop-types";

// This are hardcoded filter tags for bowls and salads

const ROUTE_PREFIX = "/unsere-salate";

export const CATEGORY_TAGS = {
    vegan: {
        hintText: "categoryItems.vegan",
        icon: "vegan",
        filter: "vegan",
        slug: "vegan",
        url: `${ROUTE_PREFIX}/vegan`,
    },
    vegetarisch: {
        hintText: "categoryItems.vegetarian",
        icon: "vegetarian",
        filter: "vegetarisch",
        slug: "vegetarisch",
        url: `${ROUTE_PREFIX}/vegetarisch`,
    },
    glutenFree: {
        hintText: "categoryItems.glutenFree",
        icon: "gluten-free",
        filter: "glutenfrei",
        slug: "glutenfrei",
        url: `${ROUTE_PREFIX}/glutenfrei`,
    },
    lowCarbs: {
        hintText: "categoryItems.lowCarbs",
        icon: "low-carb",
        filter: "weniger-als-30-kalorien",
        slug: "low-carb",
        url: `${ROUTE_PREFIX}/low-carb`,
    },
    klima: {
        hintText: "categoryItems.klima",
        icon: "sapling",
        filter: "klimafreundlich",
        slug: "klimafreundlich",
        url: `${ROUTE_PREFIX}/klimafreundlich`,
    },
};

const INTOLERANCES_TYPE = "intolerance";

export const ACTIONS = {
    TAG_FILTER: "tag_filter",
    INTOLERANCE_FILTER: "intolerance_filter",
};

const CategoryContext = createContext({});

const useCategoryContext = () => useContext(CategoryContext);

const initialState = {
    products: [],
    intolerances: [],
    isBowl: false,
    isSalad: false,
    isCoupon: false,
    isSubcategory: false,
    tags: CATEGORY_TAGS,
    filterTags: [],
    filterIntolerances: [],
};

// Adjust based on data gathered from template
const init = (initialState) => {
    let state = initialState;

    if (category_products) {
        state = {
            ...state,
            products: filterProducts(category_products, [], []),
        };
    }

    if (category) {
        const {
            bowlCategory,
            saladCategory,
            soupCategory,
            couponsCategory,
            subCategories,
        } = reactConfig;
        const isBowl = category.id === bowlCategory;
        const isSalad = category.id === saladCategory;
        const isSoup = category.id === soupCategory;
        const isCoupon = category.id === couponsCategory;
        const isSubcategory = subCategories.includes(category.id);
        state = {
            ...state,
            ...{
                isBowl,
                isSalad,
                isSoup,
                isCoupon,
                isSubcategory,
            },
        };
    }

    if (availableProductFilterTerms) {
        const intolerances = availableProductFilterTerms.filter(
            (term) => term.type === INTOLERANCES_TYPE
        );

        state = { ...state, intolerances };
    }

    return state;
};

const CategoryReducer = (state, action) => {
    const { type, payload } = action;
    switch (type) {
        case ACTIONS.TAG_FILTER:
            const { selectedTag } = payload;
            const { filterTags } = state;
            let updatedTags = filterTags;

            if (filterTags.includes(selectedTag)) {
                updatedTags = filterTags.filter((tag) => tag !== selectedTag);
            } else {
                updatedTags = [...updatedTags, selectedTag];
            }

            return {
                ...state,
                filterTags: updatedTags,
                products: filterProducts(
                    category_products,
                    updatedTags,
                    state.filterIntolerances
                ),
            };
        case ACTIONS.INTOLERANCE_FILTER:
            return {
                ...state,
                filterIntolerances: payload,
                products: filterProducts(
                    category_products,
                    state.filterTags,
                    payload
                ),
            };

        default:
            throw new Error(`No action for this ${type}`);
    }
};

const filterProducts = (products, tags = [], intolerances = []) => {
    let filteredProducts = products;

    if (tags.length) {
        const productsWithTags = products.filter((p) => {
            const productTags = p.attributes.map((attr) => attr.slug);
            return tags.every((tag) => productTags.includes(tag));
        });

        filteredProducts = productsWithTags;
    }

    if (window.location.pathname.includes("/shop")) {
        filteredProducts = products.filter((product) => {
            return !(
                product.vegan_related_product && product.vegan_type == "vegan"
            );
        });
    }

    if (intolerances.length) {
        filteredProducts = filteredProducts.filter((p) => {
            const productIntolerancesIds = p.intolerances.map(
                (intolerance) => intolerance.id
            );
            return intolerances.every(
                (intoleranceId) =>
                    !productIntolerancesIds.includes(intoleranceId)
            );
        });
    }

    return filteredProducts;
};

const CategoryProvider = ({ children }) => {
    const [state, dispatch] = useReducer(CategoryReducer, initialState, init);
    const isMainDish = state.isBowl || state.isSalad || state.isSoup;

    return (
        <CategoryContext.Provider
            value={{ state: { ...state, isMainDish }, dispatch }}>
            {children}
        </CategoryContext.Provider>
    );
};

CategoryProvider.propTypes = {
    children: PropTypes.oneOfType([
        PropTypes.arrayOf(PropTypes.node),
        PropTypes.node,
    ]).isRequired,
};

export { CategoryProvider, useCategoryContext };
